import { useState, useEffect } from "react";
import { Grid, Card, CircularProgress, MenuItem, Select, FormControl } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CategoriesApi from "api/categories";
import { handleUpload } from "utils/ImageUploader";
import SoftImageInput from "components/SoftImageInput";

function NewBrandCategory() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [mainCategory, setMainCategory] = useState("");
    const [mainCategories, setMainCategories] = useState([]);
    const [brandPosition, setBrandPosition] = useState(0);
    const [image, setImage] = useState("");
    const [file, setFile] = useState(null);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchMainCategories();
    }, []);

    const fetchMainCategories = async () => {
        const token = localStorage.getItem("token");
        try {
            const response = await CategoriesApi.GetMainCategories(token);
            setMainCategories(response.data.results || []);
        } catch (err) {
            console.error("Failed to fetch main categories:", err);
        } finally {
            setLoadingCategories(false);
        }
    };

    const handleImageChange = async (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            try {
                setImageLoading(true);
                const imageUrl = await handleUpload(selectedFile);
                setImage(imageUrl);
                setFile(imageUrl);
            } catch (err) {
                console.error("Error uploading image:", err);
                setError(t("Erreur lors de l'upload de l'image"));
            } finally {
                setImageLoading(false);
            }
        }
    };

    const handleSave = async () => {
        if (!name) {
            setError(t("Le nom est requis"));
            return;
        }
        if (!mainCategory) {
            setError(t("La catégorie principale est requise"));
            return;
        }

        setIsSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            let imageUrl = file;

            const formData = new FormData();
            formData.append("brand_category_name", name);
            formData.append("description", description);
            formData.append("main_category", mainCategory);
            formData.append("brand_position", brandPosition);
            if (imageUrl) {
                formData.append("brand_category_image", imageUrl);
            }

            await CategoriesApi.CreateBrandCategory(token, formData);
            navigate("/gestion-de-stock/brand-categories");
        } catch (err) {
            console.error("Failed to create brand category:", err);
            setError(t("Erreur lors de la création de la catégorie de marque"));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftBox mt={3} mb={4}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} lg={8}>
                        <Card>
                            <SoftBox p={3}>
                                <SoftTypography variant="h5" fontWeight="bold">
                                    {t("Nouvelle catégorie de marque")}
                                </SoftTypography>
                                <SoftBox mt={3}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={4}>
                                            <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                    {t("Nom")}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftInput
                                                placeholder={t("Nom")}
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                error={!!error && !name}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                    {t("Catégorie principale")}
                                                </SoftTypography>
                                            </SoftBox>
                                            <FormControl fullWidth variant="outlined" size="small">
                                                <Select
                                                    value={mainCategory}
                                                    onChange={(e) => setMainCategory(e.target.value)}
                                                    displayEmpty
                                                    disabled={loadingCategories}
                                                    sx={{ height: "42.75px" }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        {loadingCategories ? t("Chargement...") : t("Sélectionner une catégorie")}
                                                    </MenuItem>
                                                    {mainCategories.map((cat) => (
                                                        <MenuItem key={cat.id} value={cat.id}>
                                                            {cat.main_category_name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                    {t("Position")}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftInput
                                                type="number"
                                                placeholder={t("Position")}
                                                value={brandPosition}
                                                onChange={(e) => setBrandPosition(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                    {t("Description")}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftInput
                                                placeholder={t("Description")}
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                multiline
                                                rows={3}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <SoftImageInput
                                                label={t("Image")}
                                                image={image}
                                                onChange={handleImageChange}
                                                onClear={() => {
                                                    setImage("");
                                                    setFile(null);
                                                }}
                                                loading={imageLoading}
                                            />
                                        </Grid>
                                    </Grid>
                                </SoftBox>
                                {error && (
                                    <SoftBox mt={2}>
                                        <SoftTypography variant="caption" color="error">
                                            {error}
                                        </SoftTypography>
                                    </SoftBox>
                                )}
                                <SoftBox mt={4} display="flex" justifyContent="space-between">
                                    <SoftButton variant="gradient" color="secondary" onClick={() => navigate("/gestion-de-stock/brand-categories")}>
                                        {t("Retour")}
                                    </SoftButton>
                                    <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={isSaving}>
                                        {isSaving ? <CircularProgress size={20} color="inherit" /> : t("Enregistrer")}
                                    </SoftButton>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Grid>
                </Grid>
            </SoftBox>
            <Footer />
        </DashboardLayout>
    );
}

export default NewBrandCategory;
